import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, mergeMap, Observable, throwError, from, EMPTY } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MfaOtpPromptService } from '@services/helpers/mfa-otp-prompt.service';

/** Set on the retried request so we never infinite-loop MFA prompting. */
export const REMMI_MFA_RETRY_MARKER_HEADER = 'x-remmi-mfa-retried';

@Injectable()
export class SensitiveActionMfaInterceptor implements HttpInterceptor {
  constructor(private readonly mfaPrompt: MfaOtpPromptService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!this.isPotentialMfaChallenge(err, req)) {
          return throwError(() => err);
        }

        // The MFA challenge body can arrive as a Blob for responseType:'blob' requests
        // (e.g. CSV export), so parse it asynchronously before inspecting the flags.
        return from(this.readErrorBody(err)).pipe(
          mergeMap(body => {
            if (!this.hasMfaFlag(body)) {
              return throwError(() => err);
            }

            const payload = (body ?? {}) as Record<string, unknown>;
            const ctx = {
              message: typeof payload.message === 'string' ? payload.message : undefined,
              authentication_type: typeof payload.authentication_type === 'string' ? payload.authentication_type : undefined,
              email: typeof payload.email === 'string' ? payload.email : undefined,
            };

            return from(this.mfaPrompt.prompt(ctx)).pipe(
              // User dismissed the OTP dialog (Cancel) → abort quietly. Do NOT surface the original MFA
              // challenge as an error, otherwise every caller's error handler toasts the MFA message.
              catchError((promptErr: unknown) => {
                if (promptErr instanceof DOMException && promptErr.name === 'AbortError') {
                  return EMPTY;
                }
                return throwError(() => err);
              }),
              mergeMap(otp =>
                next.handle(
                  req.clone({
                    setHeaders: {
                      'x-mfa-otp': otp,
                      [REMMI_MFA_RETRY_MARKER_HEADER]: '1',
                    },
                  })
                )
              ),
              // Retried request (with OTP) failed for real → propagate the RETRY error (e.g. "You cannot
              // edit this role."), not the stale MFA challenge, so the caller toasts the real message.
              catchError((retryErr: unknown) => throwError(() => retryErr))
            );
          })
        );
      })
    );
  }

  /** Status / url / marker gate — everything except the (possibly async) body flag check. */
  private isPotentialMfaChallenge(err: HttpErrorResponse, req: HttpRequest<unknown>): boolean {
    if (!(err instanceof HttpErrorResponse)) {
      return false;
    }
    if (err.status !== 403 && err.status !== 422) {
      return false;
    }
    if (!req.url.includes(environment.nodeApiUrl)) {
      return false;
    }
    if (req.headers.has(REMMI_MFA_RETRY_MARKER_HEADER)) {
      return false;
    }

    const path = req.url.replace(environment.nodeApiUrl + '/' + environment.api_prefix, '');
    if (path.includes('/auth/login') || path.includes('/auth/forget-password') || path.includes('/open-user-tc')) {
      return false;
    }
    // The CSV export is a blob download that owns its own MFA prompt + retry in the component,
    // so the OTP dialog can be shown and the file saved only after the challenge clears.
    if (path.includes('/login-reports/export')) {
      return false;
    }
    return true;
  }

  /** Normalize the error body to a plain object — Blob bodies (responseType:'blob') are read as JSON text. */
  private async readErrorBody(err: HttpErrorResponse): Promise<unknown> {
    const body = err.error;
    if (body instanceof Blob) {
      try {
        return JSON.parse(await body.text());
      } catch {
        return null;
      }
    }
    return body;
  }

  private hasMfaFlag(body: unknown): boolean {
    if (!body || typeof body !== 'object') {
      return false;
    }
    return (body as { mfa_required?: boolean }).mfa_required === true || (body as { twofector?: boolean }).twofector === true;
  }
}
